import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DatePicker, Select, Table } from 'antd';
import moment from 'moment';
import dayjs from "dayjs";
import locale from 'antd/es/date-picker/locale/bn_BD';
import 'antd/dist/antd.css';
import { useAuth } from '../../../../context/AuthContext';
import swal from "sweetalert";
import {
    getSingleCaseViewApi,
    getCaseCategoriesApi,
    getCaseTypesByCategoryApi,
    getLawOfficersApi,
    getLawOfficerNamesByOfficerApi,
    getAssignResponsibleOfficersApi,
    assignLawOfficerApi,
    deleteAssignLawOfficerApi,
    updateAppealCourtEntryApi,
} from '../../../../api/master.api';

const { Option } = Select;

const AppealCourtCaseUpdateForm = () => {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        case_category: '',
        case_type: '',
        case_number: '',
        case_year: '',
        filing_date: '',
        appelier: '',
        case_type_name: '',
        category_name: '',
        case_description: '',
        ha_case_court_name: '',
        ha_case_justice_name: '',
        important_case: '',
        rule_issue_date: ''
    });
    const [errors, setErrors] = useState({});

    const [courtCategoryOptions, setCourtCategoryOptions] = useState([]);
    const [caseTypeOptions, setCaseTypeOptions] = useState([]);
    const [officerOptions, setOfficerOptions] = useState([]);

    // Related officers (High Court style - single entry + table)
    const [assignLawOfficerData, setAssignLawOfficerData] = useState([]);
    const [assignNewOfficersData, setAssignNewOfficersData] = useState([]);
    const [officersId, setOfficerId] = useState({
        law_officer_id: '',
        officer_name_id: '',
    });
    const [assignLawLoading, setAssignLawLoading] = useState(false);

    // Helpers
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        setErrors(prev => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

     const handleDateChange = (field, date) => {
        // console.log(field, date)
        if (date && moment.isMoment(date) && date.isValid()) {
            //   console.log(field, date)
            handleInputChange(field, date.format('YYYY-MM-DD'));
        } else if (date === null) {
            handleInputChange(field, '');
        } else {
            console.warn('Invalid date selected:', date);
            handleInputChange(field, '');
        }
    };

    const getSafeDateValue = (dateString) => {
        if (!dateString) return null;
        return dayjs(dateString, 'YYYY-MM-DD').isValid() ? dayjs(dateString, 'YYYY-MM-DD') : null;
    };
    const convertDateFormat = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            const [day, month, year] = dateStr.split('/');
            return `${year}-${month}-${day}`;
        }
        return dateStr;
    };

    // Fetch existing case data
    useEffect(() => {
        const fetchCaseData = async () => {
            try {
                setLoading(true);
                const res = await getSingleCaseViewApi(caseId);
                const caseData = res.data?.data;

                if (caseData) {
                    setFormData({
                        case_category: caseData.case_category || caseData.category_id || '',
                        case_type: caseData.case_type || caseData.case_type_id || '',
                        case_number: caseData.case_number || '',
                        case_year: caseData.case_year || '',
                        filing_date: convertDateFormat(caseData.filing_date) || '',
                        appelier: caseData.appelier || '',
                        case_description: caseData.case_description || '',
                        ha_case_court_name: caseData.ha_case_court_name || '',
                        ha_case_justice_name: caseData.ha_case_justice_name || '',
                        case_type_name: caseData.case_type_name || '',
                        category_name: caseData.category_name || '',
                        important_case: caseData.important_case || '',
                        rule_issue_date: convertDateFormat(caseData.rule_issue_date) || '',
                    });
                }
            } catch (err) {
                console.error(err);
                swal({
                    title: "Error",
                    text: "Failed to load case data",
                    icon: "error",
                    button: "Ok",
                });
                navigate('/report/appeal-court/all-case');
            } finally {
                setLoading(false);
            }
        };

        if (caseId) {
            fetchCaseData();
        }
    }, [caseId, navigate]);

    // Fetch Court Category Data
    useEffect(() => {
        const getCategories = async () => {
            try {
                const res = await getCaseCategoriesApi(1, 100, '');
                const items = res.data?.data?.items ?? [];
                const options = items.map((item) => ({
                    value: item.category_id,
                    label: item.category_name,
                }));
                setCourtCategoryOptions(options);
            } catch (err) {
                console.error(err);
            }
        };
        getCategories();
    }, []);

    // Fetch Case Type Data based on selected category
    useEffect(() => {
        const getCaseTypes = async () => {
            if (!formData.case_category) {
                setCaseTypeOptions([]);
                return;
            }
            try {
                const res = await getCaseTypesByCategoryApi(formData.case_category);
                const rows = res.data?.data ?? [];
                const options = rows.map((item) => ({
                    value: item.case_type_id,
                    label: item.case_type_name,
                }));
                setCaseTypeOptions(options);
            } catch (err) {
                console.error(err);
                setCaseTypeOptions([]);
            }
        };
         if (formData.case_category) {
            getCaseTypes();
        }
       
    }, [formData.case_category]);

    // Fetch Related Law Officer Options
    useEffect(() => {
        const getOfficers = async () => {
            try {
                const res = await getLawOfficersApi(1, 100, '');
                const items = res.data?.data?.items ?? [];
                const options = items.map((item) => ({
                    value: item.law_officer_id,
                    label: item.law_officer_name,
                }));
                setOfficerOptions(options);
            } catch (err) {
                console.error(err);
            }
        };
        getOfficers();
    }, []);

    // Fetch assigned officers list
    useEffect(() => {
        const fetchAssignedOfficers = async () => {
           
            try {
                const res = await getAssignResponsibleOfficersApi(parseInt(caseId, 10));
                const rows = res.data?.data ?? [];
                setAssignLawOfficerData(Array.isArray(rows) ? rows : []);
            } catch (err) {
                console.error(err);
            } finally {
                setAssignLawLoading(false);
            }
        };

        if (caseId) {
            fetchAssignedOfficers();
        }
    }, [ assignLawLoading]);

    const updateOfficer = async (value) => {
        setAssignNewOfficersData([]);
        setOfficerId({ law_officer_id: value, officer_name_id: '' });
        if (!value) return;
        try {
            const res = await getLawOfficerNamesByOfficerApi(value);
            if (res.data?.success) {
                const rows = res.data?.data ?? [];
                const options = rows.map((item) => ({
                    value: item.officer_name_id,
                    label: item.officer_name,
                }));
                setAssignNewOfficersData(options);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const addOfficer = async () => {
        if (!officersId.law_officer_id || !officersId.officer_name_id) {
            swal({
                title: "তথ্য প্রয়োজন",
                text: "উভয় কর্মকর্তা নির্বাচন করুন",
                icon: "warning",
                button: "ঠিক আছে",
            });
            return;
        }
        try {
            const response = await assignLawOfficerApi({
                case_entry_id: parseInt(caseId, 10),
                officer_name_id: parseInt(officersId.officer_name_id, 10),
                law_officer_id: parseInt(officersId.law_officer_id, 10),
            });

            if (response.data?.success) {
                setOfficerId({
                    law_officer_id: '',
                    officer_name_id: '',
                });
                setAssignNewOfficersData([]);
                setAssignLawLoading(true); // trigger refresh
            }
        } catch (error) {
            console.error('Error adding related law officer name:', error);
            swal({
                title: "ত্রুটি",
                text: "সংশ্লিষ্ট আইন কর্মকর্তার নাম যোগ করতে সমস্যা হয়েছে",
                icon: "error",
                button: "ঠিক আছে",
            });
        }
    };

    const removeOfficer = (id) => {
        deleteAssignLawOfficerApi(id)
            .then((response) => {
                if (response.data?.success) {
                    setAssignLawLoading(true);
                }
            })
            .catch((error) => {
                console.error('Error removing related law officer name:', error);
            });
    };

    const officerColumns = [
        {
            title: 'সংশ্লিষ্ট আইন কর্মকর্তা',
            dataIndex: 'law_officer_name',
            key: 'officer',
        },
        {
            title: 'সংশ্লিষ্ট আইন কর্মকর্তার নাম',
            dataIndex: 'officer_name',
            key: 'name',
        },
        {
            title: 'কার্যক্রম',
            key: 'action',
            render: (_, record) => (
                <button
                    type="button"
                    onClick={() => removeOfficer(record.officer_name_dtl_id)}
                    style={styles.removeButton}
                    onMouseOver={(e) => (e.target.style.backgroundColor = '#dc2626')}
                    onMouseOut={(e) => (e.target.style.backgroundColor = '#ef4444')}
                >
                    <i className="fas fa-trash" style={{ fontSize: '12px' }}></i>
                </button>
            ),
        },
    ];

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();



        try {
            const finalData = {
                ...formData,
                updated_by: user?.id,
                court_type_id: 7,
                case_entry_id: parseInt(caseId, 10),
            };

            //  console.log("finalData",finalData)
           

            const res = await updateAppealCourtEntryApi(parseInt(caseId, 10), finalData);
//   console.log("res",res)
            if (res.data?.success) {
                swal({
                    title: "ধন্যবাদ",
                    text: "সফলভাবে আপডেট হয়েছে",
                    icon: "success",
                    button: "ঠিক আছে",
                });
                // navigate('/report/appeal-court/all-case');
            }
        } catch (err) {
            console.error("Update failed:", err);
            swal({
                title: "ত্রুটি",
                text: "আপডেট করার সময় সমস্যা হয়েছে",
                icon: "error",
                button: "ঠিক আছে",
            });
        }
    };

    const styles = {
        container: {
            maxWidth: '1100px',
            margin: '0 auto',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e5e7eb'
        },
        header: {
            backgroundColor: '#f3f4f6',
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb'
        },
        headerTitle: {
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0
        },
        formInput: {
            width: '100%',
            padding: '8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px'
        },
        datePicker: {
            width: '100%',
            height: '36px'
        },
        searchableSelect: {
            width: '100%',
            height: '36px'
        },
        textarea: {
            width: '100%',
            padding: '8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '14px',
            resize: 'none'
        },
        label: {
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '8px',
            display: 'block'
        },
        removeButton: {
            width: '32px',
            height: '32px',
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
        },
        errorText: {
            color: '#ef4444',
            fontSize: '12px',
            marginTop: '4px'
        },
        submitButton: {
            backgroundColor: '#0d9488',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            minWidth: '160px'
        },
        tableSection: {
            marginTop: '16px'
        },
        tableTitle: {
            fontSize: '14px',
            fontWeight: '600',
            marginBottom: '8px'
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '32px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
                <p style={{ marginTop: '12px', color: '#4b5563' }}>Loading case data...</p>
            </div>
        );
    }

    return (
        <div className='page-wrapper'>
            <div style={styles.container}>
                <div style={styles.header}>
                    <h1 style={styles.headerTitle}>আপিল বিভাগ - মামলা সংশোধন</h1>
                </div>

                <div style={{ padding: '24px' }}>
                    <form onSubmit={handleSubmit}>
                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>মামলার ক্যাটেগরি {formData.category_name ? `(${formData.category_name})` : ''}</label>
                            <Select
                                style={styles.searchableSelect}
                                placeholder="--- নির্বাচন করুন ---"
                                // value={formData.case_category || undefined}
                                onChange={(value) => handleInputChange('case_category', value)}
                                showSearch
                                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                allowClear
                            >
                                {courtCategoryOptions?.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>মামলার শ্রেণী/কেস-টাইপ {formData.case_type_name ? `(${formData.case_type_name})` : ''}</label>
                            <Select
                                style={styles.searchableSelect}
                                placeholder="--- নির্বাচন করুন ---"
                                // value={formData.case_type || undefined}
                                onChange={(value) => handleInputChange('case_type', value)}
                                showSearch
                                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                allowClear
                            >
                                {caseTypeOptions?.map((option) => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>মামলার নং {formData.case_number ? `(${formData.case_number})` : ''}</label>
                            <input
                                type="text"
                                placeholder="(Type digits in English)"
                                style={styles.formInput}
                                value={formData.case_number}
                                onChange={(e) => handleInputChange('case_number', e.target.value)}
                            />
                        </div>

                        <div className="row" style={{ marginBottom: '16px' }}>
                            <div className="col-md-4" style={{ marginBottom: '16px' }}>
                               <label style={styles.label}>বছর {formData.case_year ? `(${formData.case_year})` : ''}</label>

                                {/* <DatePicker
                                    picker="year"
                                    placeholder="--- নির্বাচন করুন ---"
                                    style={{ width: "100%" }}
                                      value={getSafeDateValue(formData.case_year)}
                                    value={formData.case_year ? moment(formData.case_year, "YYYY") : null}
                                    onChange={(date, dateString) => handleInputChange("case_year", dateString)}
                                /> */}
                                   <DatePicker
                                picker="year"
                                placeholder="--- নির্বাচন করুন ---"
                                style={{ width: "100%" }}
                                // value={formData.case_year ? dayjs(formData.case_year, "YYYY") : null}
                                onChange={(date, dateString) => handleInputChange("case_year", dateString)}
                            />

                            </div>
                            <div className="col-md-4">
                                <label style={styles.label}>মামলা দায়েরের তারিখ {formData.filing_date && (
                                <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px', fontWeight: 'normal' }}>
                                    ({moment(formData.filing_date).format('DD/MM/YYYY')})
                                </span>
                            )}</label>
                             
                                 <DatePicker
                        style={styles.datePicker}
                        placeholder="দিন/মাস/বছর"
                        value={getSafeDateValue(formData.filing_date)}
                        onChange={(date) => handleDateChange('filing_date', date)}
                        format="DD/MM/YYYY"
                        locale={locale}
                        allowClear={true}
                        inputReadOnly={false}
                        showToday={true}
                        showTime={false}
                        status={(formData.filing_date && !getSafeDateValue(formData.filing_date)) ? 'error' : ''}
                        onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue) {
                                const parsedDate = moment(inputValue, 'DD/MM/YYYY');
                                if (parsedDate.isValid()) {
                                    handleDateChange('filing_date', parsedDate);
                                }
                            }
                        }}
                    />
                                

                            </div>
                            <div className="col-md-4">
                                 <label style={styles.label}>রুল ইস্যুর তারিখ {formData.rule_issue_date && (
                                <span style={{ color: '#666', fontSize: '12px', marginLeft: '8px', fontWeight: 'normal' }}>
                                    ({moment(formData.rule_issue_date).format('DD/MM/YYYY')})
                                </span>
                            )}</label>
                                {/* <label style={styles.label}>রুল ইস্যুর তারিখ </label> */}
                               <DatePicker
                        style={styles.datePicker}
                        placeholder="দিন/মাস/বছর"
                        value={getSafeDateValue(formData.rule_issue_date)}
                        onChange={(date) => handleDateChange('rule_issue_date', date)}
                        format="DD/MM/YYYY"
                        locale={locale}
                        allowClear={true}
                        inputReadOnly={false}
                        showToday={true}
                        showTime={false}
                        status={(formData.rule_issue_date && !getSafeDateValue(formData.rule_issue_date)) ? 'error' : ''}
                        onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (inputValue) {
                                const parsedDate = moment(inputValue, 'DD/MM/YYYY');
                                if (parsedDate.isValid()) {
                                    handleDateChange('rule_issue_date', parsedDate);
                                }
                            }
                        }}
                    />

                            </div>
                            <div className="col-md-4">
                                <label style={styles.label}>গুরুত্বপূর্ণ মামলা ({formData.important_case == '1' ? 'হ্যাঁ' : 'না'})</label>

                                <select
                                    className="border p-2 rounded"
                                    style={styles.formInput}
                                    // value={formData.important_case}
                                    onChange={(e) =>
                                        handleInputChange("important_case", e.target.value)
                                    }
                                >
                                    <option value="">--- নির্বাচন করুন ---</option>
                                    <option value="1">হ্যাঁ</option>
                                    <option value="0">না</option>
                                </select>
                            </div>

                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>আপিলকারী </label>
                            <input
                                type="text"
                                style={styles.formInput}
                                value={formData.appelier}
                                onChange={(e) => handleInputChange('appelier', e.target.value)}
                            />

                        </div>

                        <div className="row" style={{ marginBottom: '16px' }}>
                            <div className="col-md-6" style={{ marginBottom: '16px' }}>
                                <label style={styles.label}>আদালতের নাম</label>
                                <input
                                    type="text"
                                    style={styles.formInput}
                                    value={formData.ha_case_court_name}
                                    onChange={(e) => handleInputChange('ha_case_court_name', e.target.value)}
                                />
                            </div>
                            <div className="col-md-6">
                                <label style={styles.label}>Justice Name</label>
                                <input
                                    type="text"
                                    style={styles.formInput}
                                    value={formData.ha_case_justice_name}
                                    onChange={(e) => handleInputChange('ha_case_justice_name', e.target.value)}
                                />
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={styles.label}>মামলার বিবরণ</label>
                            <textarea
                                style={{ ...styles.textarea, height: '128px' }}
                                value={formData.case_description}
                                onChange={(e) => handleInputChange('case_description', e.target.value)}
                            />
                        </div>
                        <div className="text-center mt-4 mb-4">
                            <button
                                type="submit"
                                style={styles.submitButton}
                                onMouseOver={(e) => e.target.style.backgroundColor = '#0f766e'}
                                onMouseOut={(e) => e.target.style.backgroundColor = '#0d9488'}
                                onClick={handleSubmit}
                            >
                                আপডেট করুন
                            </button>
                        </div>
                        {/* Related Officers Section - single entry with table */}
                        <div style={{ marginBottom: '16px' }} className="border p-2">
                            <div className="row mb-2">
                                <div className="col-md-6">
                                    <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                        সংশ্লিষ্ট আইন কর্মকর্তা
                                    </label>
                                </div>
                                <div className="col-md-6">
                                    <label style={{ ...styles.label, display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                        সংশ্লিষ্ট আইন কর্মকর্তার নাম
                                    </label>
                                </div>
                            </div>

                            <div className="row mb-2">
                                <div className="col-md-6">
                                    <Select
                                        style={styles.searchableSelect}
                                        placeholder="--- নির্বাচন করুন ---"
                                        value={officersId.law_officer_id || undefined}
                                        onChange={(value) => updateOfficer(value)}
                                        showSearch
                                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        allowClear
                                    >
                                        {officerOptions?.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="col-md-6">
                                    <Select
                                        style={styles.searchableSelect}
                                        placeholder="--- নির্বাচন করুন ---"
                                        value={officersId.officer_name_id || undefined}
                                        onChange={(value) =>
                                            setOfficerId((prev) => ({
                                                ...prev,
                                                officer_name_id: value,
                                            }))
                                        }
                                        showSearch
                                        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                                        allowClear
                                    >
                                        {(assignNewOfficersData || [])?.map((option) => (
                                            <Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </div>

                            <button type="button" onClick={addOfficer} className="bg-success" style={{ ...styles.submitButton, minWidth: 'auto', padding: '8px 12px' }}>
                                যোগ করুন
                            </button>

                            <div style={styles.tableSection}>
                                <div style={styles.tableTitle}>সংশ্লিষ্ট আইন কর্মকর্তাদের তালিকা</div>
                                <Table
                                    columns={officerColumns}
                                    dataSource={(assignLawOfficerData || []).map((officer, index) => ({
                                        ...officer,
                                        key: index
                                    }))}
                                    pagination={false}
                                    size="small"
                                    locale={{ emptyText: 'কোন তথ্য পাওয়া যায়নি' }}
                                />
                            </div>
                        </div>


                    </form>
                </div>
            </div>
        </div>
    );
};

export default AppealCourtCaseUpdateForm;

